import type { NodeLayout } from './layout';
import { iconSvg } from './icons';
import { STATUS_BUTTON_ICON } from './renderer';

export interface PanelHandle {
  /** Re-read the layouts map and re-render the list (preserves the current search query). */
  refresh: () => void;
}

const FOCUS_ZOOM = 1.6;

export function setupPanel(
  layouts:    Map<string, NodeLayout>,
  focusOn:    (x: number, y: number, targetScale?: number) => void,
  pulseNode:  (id: string) => void,
  selectNode: (id: string) => void,
): PanelHandle {
  const search = document.getElementById('node-search') as HTMLInputElement;
  const list   = document.getElementById('node-list')   as HTMLUListElement;

  let query = '';

  const render = (): void => {
    const q = query.toLowerCase();

    // DFS pre-order from each root so the list mirrors the project hierarchy.
    // Roots iterate in `layouts` insertion order (which matches creation order).
    const ordered: NodeLayout[] = [];
    const seen = new Set<string>();
    const visit = (layout: NodeLayout): void => {
      if (seen.has(layout.id)) return; // safety against accidental cycles
      seen.add(layout.id);
      ordered.push(layout);
      for (const childId of layout.childIds) {
        const child = layouts.get(childId);
        if (child) visit(child);
      }
    };
    for (const layout of layouts.values()) {
      if (layout.parentId === null) visit(layout);
    }

    list.innerHTML = '';
    for (const layout of ordered) {
      if (q !== '' && !layout.label.toLowerCase().includes(q)) continue;

      const li = document.createElement('li');
      li.dataset.id = layout.id;
      li.style.paddingLeft = `${14 + layout.level * 12}px`;
      const statusIcon = iconSvg(STATUS_BUTTON_ICON[layout.status], 14);
      li.innerHTML =
        `<span class="status" style="color:var(--status-${layout.status})">${statusIcon}</span>` +
        `<span class="level">L${layout.level}</span>` +
        `<span class="label">${escapeHtml(layout.label)}</span>`;
      list.appendChild(li);
    }
  };

  // Coalesce render() calls to one per frame — keystrokes and external
  // refreshes can arrive in bursts; we don't need a DOM rebuild for each.
  let renderQueued = false;
  const scheduleRender = (): void => {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      render();
    });
  };

  // Click delegation — survives re-renders.
  list.addEventListener('click', (e) => {
    const li = (e.target as HTMLElement).closest('li');
    if (!li || !li.dataset.id) return;
    const layout = layouts.get(li.dataset.id);
    if (!layout) return;
    focusOn(layout.x, layout.y, FOCUS_ZOOM);
    pulseNode(layout.id);
    selectNode(layout.id);
  });

  // Search-match pulse: 250 ms after the user stops typing, pulse every node
  // whose label still matches the query — capped to avoid mass-pulse sweeps.
  const SEARCH_PULSE_DEBOUNCE_MS = 250;
  const SEARCH_PULSE_MAX_MATCHES = 30;
  let searchPulseTimer: number | null = null;

  search.addEventListener('input', () => {
    query = search.value;
    scheduleRender();

    if (searchPulseTimer !== null) clearTimeout(searchPulseTimer);
    const trimmed = query.trim();
    if (trimmed === '') return;

    searchPulseTimer = window.setTimeout(() => {
      searchPulseTimer = null;
      const q = trimmed.toLowerCase();
      const matches: string[] = [];
      for (const layout of layouts.values()) {
        if (layout.label.toLowerCase().includes(q)) {
          matches.push(layout.id);
          if (matches.length > SEARCH_PULSE_MAX_MATCHES) return; // skip when too broad
        }
      }
      for (const id of matches) pulseNode(id);
    }, SEARCH_PULSE_DEBOUNCE_MS);
  });

  render();

  return { refresh: scheduleRender };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
